import type { EventEmitter } from '@stencil/core';
import { globalWindow } from '../../../../../utils/global-window';
import { getActiveBreakpoints } from '../../../../../utils/media';
import type { labelProviderInterface } from '../../../../LabelProviderInterface';
import { ChatIcons, type ConversationalSearchAnswer, type ConversationalSearchProductSuggestions } from '../../conversational-search-types/conversational-search-types.type';
import { getCryptoRandomInt } from '../../../../../utils/cryptohelpers';

/**
  This function select unique random elements from array
*/
export const getRandomUniqueItemsFromArray = (
  array: string[],
  itemsToPick: number,
): string[] => {
    const selectedItemsIndexes = new Set<number>();

    while (selectedItemsIndexes.size < itemsToPick) {
      selectedItemsIndexes.add(getCryptoRandomInt(array.length));
    }

    const arrayOfUniqueRandomItems = Array.from(selectedItemsIndexes).map((index) => array[index]);

    return arrayOfUniqueRandomItems;
};

/**
  Wrapper for window.dispatchEvent
*/
export const eventDispatcherForFunctionalComponents = (
  eventName: string,
  detailData?: { [key: string]: any }, //eslint-disable-line
) =>
  window.dispatchEvent(
    new CustomEvent(eventName, {
      detail: detailData,
    }),
  );

/**
  Default dispatchEvent for common use case
*/
export const ctaEventDispatcher = (ctaName: string, keyword: string, ctaLink?: string, ctaLocation?: string) =>
  eventDispatcherForFunctionalComponents('conversational-search:ga-cta', {
    ctaName,
    keyword,
    ctaLink,
    ctaLocation
  });

/**
  Default dispatchEvent for common use case
*/
export const ctaSuggestionsEventDispatcher = (type: string, suggestionName: string, category: string, url: string, keyword: string, index: number) => {
    const clickType = type === 'contextmenu' ? 'Right' : 'Left';
    eventDispatcherForFunctionalComponents('conversational-search:ga-suggestion', {
      clickType,
      suggestionName,
      category,
      url,
      keyword,
      index
    });
};

/**
  Scrolls to last element or when it is missing to the bottom
  using timeout to push scrollTo invocation in event loop
*/
export const scrollWidget = (elementForScroll: Element, lastElementToFindName: string) => {
    const componentsArray = elementForScroll?.querySelectorAll(lastElementToFindName) as NodeListOf<HTMLElement>;
    const lastComponent = componentsArray?.[componentsArray.length - 1];

    return window.setTimeout(() => {
        elementForScroll.scrollTo({
          top: lastComponent?.offsetTop ?? elementForScroll?.scrollHeight,
          behavior: 'smooth',
        });
    }, 0);
};

export const buildObsoleteProductsObject = (
  suggestions: ConversationalSearchProductSuggestions[]
) => {
    const toBeDiscontinuedProducts = suggestions?.filter(
      suggestion => suggestion?.commStatus?.key?.includes('DISCONTINUED')
    );

    return toBeDiscontinuedProducts?.length ? {
      productIds: toBeDiscontinuedProducts.map(product => product?.productId),
      commStatuses: toBeDiscontinuedProducts.map(product => product?.commStatus),
    } : undefined;
};

export const upperCaseFirstLetter = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

export const prepareSubstitutionToolUrl = (
  brandUrl: string,
  country: string,
  language: string,
  productIds: string | string[],
) =>
{
    const productIdsString = Array.isArray(productIds) ? productIds?.join(',') : productIds;

    return `${brandUrl}/${country}/${language}/product-substitution/${productIdsString}`;
};

export const substitutionCallback = (
  redirectionUrl: string,
  term: string,
  location: string
) => {
    eventDispatcherForFunctionalComponents('conversational-search:ga-cta', {
      ctaLocation: location,
      ctaName: 'Product Substitution Tool',
      ctaLink: redirectionUrl,
      ctaCategory: 'Tool link',
      keyword: term,
    });
};

export const saltGenerator = (numberRange: number): number => getCryptoRandomInt(numberRange + 1);

export const focusNavigationElement = () => {
    const searchElement = document.querySelector('uiaas-search')?.shadowRoot;
    const item = searchElement?.querySelector('nav .active') as HTMLElement;

    item?.focus({ preventScroll: true });
};

export const typeIdentifier = (
  data: ConversationalSearchAnswer['followups'] | ConversationalSearchAnswer['results'],
  type: string
): boolean => data?.some(item => item.type === type);

export const getByType = <T extends { type: string }>(
  data: T[],
  type: string
): T => data?.find(item => item.type === type);

export const getAllByType = <T extends { type: string }>(
  data: T[],
  type: string
): T[] => data?.filter(item => item.type === type);

/**
  GenAI specific function to check if the screen is medium mobile
*/
export const isMediumMobile = () => {
    const { medium, large, xLarge } = getActiveBreakpoints();
    const isMediumMobile = globalWindow.matchMedia('screen and (min-width: 450px)').matches;

    return isMediumMobile && !(medium || large || xLarge);
};

export const answerViewTypes = {
  // BackEnd Types
  Categories: 'conversation/followup/categories',
  CharacteristicsMultiValue: 'conversation/followup/characteristics/multivalue',
  CharacteristicsSingleValue: 'conversation/followup/characteristics/singlevalue',
  Irrelevance: 'conversation/followup/warning/irrelevance',
  SuggestionResults: 'conversation/result/product',
  HierarchyResults: 'conversation/result/product-hierarchy',
  // Internal types
  UserMessage: 'conversation/user/message',
  ErrorMessage: 'conversation/error',
  FollowUpSatisfied: 'conversation/followup/satisfied',
  FollowUpNotSatisfied: 'conversation/followup/not-satisfied',
  NotDetailedAnswer: 'conversation/not-detailed-answer',
  Fallback: 'conversation/fallback',
};

export const fixedCarouselElementsWidth = {
  bannerLarge: 700,
  bannerMedium: 400,
  bannerSmall: 250, // Fix for QDS
  conversationLarge: 230,
  conversationSmall: undefined, // Dynamic
};

export const feedbackElements = (labelProvider: labelProviderInterface) => [
  {
    variant: 'Like',
    icon: ChatIcons.THUMB_UP_ICON,
    text: labelProvider('conversational.search.thumb.up', 'Like'),
  },
  {
    variant: 'Dislike',
    icon: ChatIcons.THUMB_DOWN_ICON,
    text: labelProvider('conversational.search.thumb.down', 'Dislike'),
  }
];

/**
  Extend QDS Carousel event emitter to track carousel chevron clicks.
  It is not possible to wrap QDS Carousel in a web component with additional events logic:
  critical issues with carouse internal logic.
*/
export const qdsCarouselEventEmitter = (
  keyword: string,
  element: Element,
  context: Object,
  additionalEmitter?: EventEmitter<{}>
): {
    attatchEvents: () => void,
    cleanEvents: () => void
  } => {
    if (!element) return { attatchEvents: () => {}, cleanEvents: () => {} };

    const event = new CustomEvent('conversational-search:ga-icon-action', {
      bubbles: true,
      composed: true,
      detail: { keyword: keyword, actionItem: 'chevron', actionType: 'click' },
    });

    const clickCallback = () => {
        window.dispatchEvent(event);
        additionalEmitter?.emit();
    };

    const boundClickCallback = clickCallback.bind(context);

    return {
      attatchEvents: () => {
          element.removeEventListener('click', boundClickCallback);
          element.addEventListener('click', boundClickCallback);
      },
      cleanEvents: () => element.removeEventListener('click', boundClickCallback)
    };
};